import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Expenses = () => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    category: 'salaries',
    description: '',
    amount: 0,
    month: new Date().toISOString().slice(0, 7), // YYYY-MM
    paid: false
  });
  const [editingId, setEditingId] = useState(null);

  const categories = [
    { value: 'salaries', label: 'Заплати' },
    { value: 'accounting', label: 'Счетоводство' },
    { value: 'rent', label: 'Наем' },
    { value: 'utilities', label: 'Комунални услуги' },
    { value: 'equipment', label: 'Оборудване' },
    { value: 'marketing', label: 'Маркетинг' },
    { value: 'other', label: 'Друго' }
  ];

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const res = await axios.get(`${API}/expenses`);
      setExpenses(res.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/expenses/${editingId}`, formData);
        toast.success('Разходът бе актуализиран!');
      } else {
        await axios.post(`${API}/expenses`, formData);
        toast.success('Разходът бе добавен!');
      }
      fetchExpenses();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Сигурни ли сте?')) {
      try {
        await axios.delete(`${API}/expenses/${id}`);
        toast.success('Разходът бе изтрит!');
        fetchExpenses();
      } catch (error) {
        toast.error('Грешка при изтриване');
      }
    }
  };

  const handleEdit = (expense) => {
    setFormData(expense);
    setEditingId(expense.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      category: 'salaries',
      description: '',
      amount: 0,
      month: new Date().toISOString().slice(0, 7),
      paid: false
    });
    setEditingId(null);
  };

  const getCategoryLabel = (cat) => {
    const category = categories.find(c => c.value === cat);
    return category ? category.label : cat;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      salaries: '#3b82f6',
      accounting: '#8b5cf6',
      rent: '#10b981',
      utilities: '#f59e0b',
      equipment: '#06b6d4',
      marketing: '#ec4899',
      other: '#6b7280'
    };
    return colors[cat] || '#6b7280';
  };

  // Group by month
  const groupedExpenses = expenses.reduce((acc, expense) => {
    if (!acc[expense.month]) {
      acc[expense.month] = [];
    }
    acc[expense.month].push(expense);
    return acc;
  }, {});

  const sortedMonths = Object.keys(groupedExpenses).sort().reverse();

  return (
    <div className="page-container" data-testid="expenses-page">
      <div className="page-header">
        <h1 className="page-title">Разходи</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-expense-btn">
              <Plus size={20} /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? t('edit') : t('add')} Разход</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="expense-form" data-testid="expense-form">
              <div className="form-field">
                <label className="field-label">Категория *</label>
                <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-field">
                <label className="field-label">Описание *</label>
                <Input
                  placeholder="напр. Заплати за ноември 2025"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </div>
              <div className="form-field">
                <label className="field-label">Сума (лв) *</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="напр. 5000.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})}
                  required
                />
              </div>
              <div className="form-field">
                <label className="field-label">Месец *</label>
                <Input
                  type="month"
                  value={formData.month}
                  onChange={(e) => setFormData({...formData, month: e.target.value})}
                  required
                />
              </div>
              <div className="form-field">
                <label className="permission-item">
                  <Checkbox
                    checked={formData.paid}
                    onCheckedChange={(val) => setFormData({...formData, paid: val})}
                  />
                  <span>Платено</span>
                </label>
              </div>
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" data-testid="save-expense-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="expenses-by-month">
        {sortedMonths.map(month => {
          const monthExpenses = groupedExpenses[month];
          const monthTotal = monthExpenses.reduce((sum, e) => sum + e.amount, 0);
          const paidTotal = monthExpenses.filter(e => e.paid).reduce((sum, e) => sum + e.amount, 0);
          const unpaidTotal = monthTotal - paidTotal;

          return (
            <div key={month} className="month-section" data-testid={`month-${month}`}>
              <div className="month-header">
                <h3>{new Date(month + '-01').toLocaleDateString('bg-BG', { year: 'numeric', month: 'long' })}</h3>
                <div className="month-stats">
                  <span className="stat">Общо: <strong>{monthTotal.toFixed(2)} лв</strong></span>
                  <span className="stat paid">Платено: <strong>{paidTotal.toFixed(2)} лв</strong></span>
                  <span className="stat unpaid">Неплатено: <strong>{unpaidTotal.toFixed(2)} лв</strong></span>
                </div>
              </div>

              <div className="expenses-grid">
                {monthExpenses.map(expense => (
                  <div key={expense.id} className="expense-card" data-testid={`expense-${expense.id}`}>
                    <div className="expense-card-header">
                      <div className="expense-category" style={{ backgroundColor: `${getCategoryColor(expense.category)}20`, color: getCategoryColor(expense.category) }}>
                        {getCategoryLabel(expense.category)}
                      </div>
                      <div className="expense-status">
                        {expense.paid ? (
                          <span className="status-paid"><CheckCircle size={16} /> Платено</span>
                        ) : (
                          <span className="status-unpaid"><XCircle size={16} /> Неплатено</span>
                        )}
                      </div>
                    </div>
                    <div className="expense-card-body">
                      <p className="expense-description">{expense.description}</p>
                      <div className="expense-amount">{expense.amount.toFixed(2)} лв</div>
                    </div>
                    <div className="expense-card-footer">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(expense)} data-testid={`edit-expense-${expense.id}`}>
                        <Edit size={16} /> {t('edit')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.id)} data-testid={`delete-expense-${expense.id}`}>
                        <Trash2 size={16} /> {t('delete')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Expenses;
